import { COOKIE_CONSENT_STORAGE_KEY_PREFIX } from './constants/constants';
import { buildCookieConsentMap, NON_ESSENTIAL_COOKIES } from '../../constants/cookieManagement';
import { Cookies } from './cookies';

export const hasGlobalPrivacyControlSignal = () =>
    typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;

export const getUserCookieConsent = (eventId) => {
    const storageKey = `${COOKIE_CONSENT_STORAGE_KEY_PREFIX}${eventId}`;
    try {
        return JSON.parse(localStorage.getItem(storageKey));
    } catch (e) {
        return null;
    }
};

export const saveUserCookieConsent = (eventId, consent) => {
    const storageKey = `${COOKIE_CONSENT_STORAGE_KEY_PREFIX}${eventId}`;
    localStorage.setItem(storageKey, JSON.stringify(consent));
};

/**
 * Deletes cookies by category using the patterns defined in `Cookies`.
 * Patterns may include `__ID__` placeholders, for example `session___ID__`.
 * Only first-party cookies (from `document.cookie`) can be deleted.
 * Third-party cookies cannot be deleted due to browser restrictions.
 *
 * @example
 * // Delete performance and marketing cookies
 * deleteCookies([COOKIE_CATEGORY.PERFORMANCE, COOKIE_CATEGORY.MARKETING]);
 *
 * @param {string[]} categories - Array of cookie category keys from COOKIE_CATEGORY
 */
export const deleteCookies = (categories) => {
    const cookiesToDelete = categories.reduce((acc, category) => {
        if (Cookies[category]) {
            acc.push(...Cookies[category]);
        }
        return acc;
    }, []);

    // Get all existing cookies from document.cookie (first-party only)
    const existingCookies = document.cookie ? document.cookie.split('; ').map((cookie) => cookie.split('=')[0]) : [];

    // Resolve actual cookie names, handling __ID__ placeholders efficiently
    const cookiePatterns = cookiesToDelete
        .filter((cookieName) => cookieName.includes('__ID__'))
        .map((cookiePattern) => new RegExp(`^${cookiePattern.replace('__ID__', '.*')}$`));

    const exactCookieNames = cookiesToDelete.filter((cookieName) => !cookieName.includes('__ID__'));

    const resolvedCookieNames = existingCookies.filter(
        (cookieName) => exactCookieNames.includes(cookieName) || cookiePatterns.some((regex) => regex.test(cookieName)),
    );

    // Delete first-party cookies
    resolvedCookieNames.forEach((cookieName) => {
        const expireDate = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
        // Delete for current path and domain
        document.cookie = `${cookieName}=; ${expireDate}; path=/`;
        // Also try to delete for root domain
        document.cookie = `${cookieName}=; ${expireDate}; path=/; domain=${window.location.hostname}`;
        // Try with leading dot for subdomain coverage
        const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
        document.cookie = `${cookieName}=; ${expireDate}; path=/; domain=.${rootDomain}`;
    });
};

/**
 * Applies Global Privacy Control consent by declining all non-essential categories.
 *
 * @param {string} eventId - The event ID to associate the consent with
 * @returns {boolean} True when GPC consent was applied, otherwise false
 */
export const applyGlobalPrivacyControlConsent = (eventId) => {
    if (typeof window === 'undefined' || !hasGlobalPrivacyControlSignal()) {
        return false;
    }
    saveUserCookieConsent(eventId, buildCookieConsentMap(NON_ESSENTIAL_COOKIES, false));
    deleteCookies(NON_ESSENTIAL_COOKIES);
    return true;
};
